import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import Tooltip from "@mui/material/Tooltip";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import WorkspacesOutlinedIcon from "@mui/icons-material/WorkspacesOutlined";
import ArrowForwardOutlinedIcon from "@mui/icons-material/ArrowForwardOutlined";
import Page from "../Components/Page";
import { axiosInstance } from "../Components/axiosInstance.js";
import { useAuth } from "../Components/Auth/useAuth";
import { formatDelegationScope } from "../portal/aggregatorGrowerContext.js";

function apiError(error, fallback) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((item) => item.msg).filter(Boolean).join("; ") || fallback;
  return error?.message || fallback;
}

function formatDate(value, withTime = false) {
  if (!value) return "Not available";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not available";
  return new Intl.DateTimeFormat("en-CA", {
    month: "short",
    day: "numeric",
    year: "numeric",
    ...(withTime ? { hour: "numeric", minute: "2-digit" } : {}),
  }).format(date);
}

function initials(firstName, lastName, fallback = "") {
  const value = [firstName, lastName].filter(Boolean).map((part) => part.trim()[0]).join("");
  return (value || fallback.trim()[0] || "U").toUpperCase();
}

function displayName(userData, fallback) {
  const fromProfile = [userData?.first_name, userData?.last_name].filter(Boolean).join(" ").trim();
  return fromProfile || fallback || userData?.email || "Grower";
}

function titleCaseStatus(value, fallback = "Active") {
  const normalized = String(value || fallback).trim().toLowerCase();
  return normalized ? normalized.charAt(0).toUpperCase() + normalized.slice(1) : fallback;
}

const STATUS_STYLE = {
  ACTIVE: "border-emerald-200 bg-emerald-50 text-emerald-800",
  OPTED_IN: "border-emerald-200 bg-emerald-50 text-emerald-800",
  SCHEDULED: "border-blue-200 bg-blue-50 text-blue-800",
  EXPIRED: "border-slate-200 bg-slate-100 text-slate-600",
  REVOKED: "border-red-200 bg-red-50 text-red-800",
  PENDING: "border-amber-200 bg-amber-50 text-amber-900",
  PENDING_APPROVAL: "border-amber-200 bg-amber-50 text-amber-900",
  REJECTED: "border-red-200 bg-red-50 text-red-800",
};

const STATUS_LABEL = {
  OPTED_IN: "Enrolled",
  PENDING_APPROVAL: "Approval pending",
  REJECTED: "Not approved",
  REVOKED: "Withdrawn",
};

function StatusTag({ status, label }) {
  const normalized = String(status || "UNKNOWN").toUpperCase();
  return (
    <span className={`rounded-[4px] border px-2 py-1 text-xs font-bold ${STATUS_STYLE[normalized] || STATUS_STYLE.EXPIRED}`}>
      {label || normalized.charAt(0) + normalized.slice(1).toLowerCase()}
    </span>
  );
}

function Detail({ icon, label, value, action }) {
  return (
    <div className="min-w-0 border-l-2 border-slate-200 pl-3">
      <div className="flex items-center gap-1.5 text-xs font-bold uppercase text-slate-500">
        {icon}{label}
      </div>
      <div className="mt-1 flex min-w-0 items-center gap-2 text-sm font-semibold text-slate-800">
        <span className="min-w-0 break-all">{value}</span>{action}
      </div>
    </div>
  );
}

function DelegationRow({ delegation }) {
  const name = delegation.aggregator_display_name || delegation.aggregator_email;
  const status = delegation.effective_status || delegation.status;
  const scopes = delegation.scope?.length ? delegation.scope : ["all"];
  const dateDescription = delegation.starts_at || delegation.ends_at
    ? `${delegation.starts_at ? `Starts ${formatDate(delegation.starts_at)}` : "Effective immediately"} · ${delegation.ends_at ? `Ends ${formatDate(delegation.ends_at)}` : "No expiry"}`
    : "Effective immediately · No expiry";

  return (
    <article className="rounded-[8px] border border-slate-200 bg-white p-4">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <Avatar sx={{ bgcolor: "#e8f4ec", color: "#237a43", fontSize: 15, fontWeight: 800, height: 42, width: 42 }}>
            {initials(null, null, name)}
          </Avatar>
          <div className="min-w-0">
            <h3 className="break-words text-base font-black text-slate-900">{name}</h3>
            {name.toLowerCase() !== String(delegation.aggregator_email || "").toLowerCase() ? (
              <p className="mt-0.5 break-all text-sm text-slate-500">{delegation.aggregator_email}</p>
            ) : null}
          </div>
        </div>
        <StatusTag status={status} />
      </div>

      <div className="mt-4 border-t border-slate-200 pt-4">
        <div className="text-xs font-bold uppercase text-slate-500">Granted access</div>
        <div className="mt-2 flex flex-wrap gap-2">
          {scopes.map((scope) => (
            <span className="inline-flex items-center gap-1 rounded-[4px] border border-slate-200 bg-[#f8faf8] px-2 py-1 text-xs font-semibold text-slate-700" key={scope}>
              <CheckCircleOutlineIcon sx={{ fontSize: 14 }} /> {formatDelegationScope(scope)}
            </span>
          ))}
        </div>
        <p className="mt-3 flex items-start gap-2 text-sm text-slate-500">
          <ScheduleOutlinedIcon sx={{ fontSize: 18, marginTop: "1px" }} /> {dateDescription}
        </p>
      </div>
    </article>
  );
}

function ProjectParticipationRow({ enrollment }) {
  const snapshot = enrollment.consent_snapshot?.project || {};
  const projectPublicId = enrollment.project_public_id || snapshot.public_id;
  const projectName = enrollment.project_name || snapshot.name || "Project";
  const partnerName = enrollment.partner_name || snapshot.partner?.name || "Project partner";
  const startsAt = enrollment.project_starts_at || snapshot.starts_at;
  const endsAt = enrollment.project_ends_at || snapshot.ends_at;
  const status = String(enrollment.consent_status || "UNKNOWN").toUpperCase();
  const statusDate = status === "OPTED_IN"
    ? enrollment.approved_at || enrollment.consented_at
    : status === "PENDING_APPROVAL"
      ? enrollment.requested_at || enrollment.consented_at
      : enrollment.reviewed_at || enrollment.revoked_at;
  const statusDateLabel = status === "OPTED_IN"
    ? "Approved"
    : status === "PENDING_APPROVAL"
      ? "Requested"
      : status === "REJECTED"
        ? "Reviewed"
        : "Updated";
  const timeline = startsAt || endsAt
    ? `${startsAt ? formatDate(startsAt) : "Start date pending"} - ${endsAt ? formatDate(endsAt) : "No end date"}`
    : "Timeline to be confirmed";

  return (
    <article className="grid grid-cols-1 gap-4 py-5 md:grid-cols-[minmax(0,1fr)_minmax(220px,0.55fr)_auto] md:items-center">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="break-words text-base font-black text-slate-950">{projectName}</h3>
          <StatusTag label={STATUS_LABEL[status]} status={status} />
        </div>
        <p className="mt-1 text-sm text-slate-500">{partnerName}</p>
        {enrollment.review_notes && status === "REJECTED" ? (
          <p className="mt-2 text-sm leading-5 text-red-700">Administrator note: {enrollment.review_notes}</p>
        ) : null}
      </div>
      <div className="min-w-0 text-sm text-slate-600">
        <div className="flex items-center gap-2 font-semibold text-slate-800">
          <CalendarMonthOutlinedIcon sx={{ color: "#64748b", fontSize: 17 }} />
          <span>{timeline}</span>
        </div>
        {statusDate ? <p className="mt-1 pl-[25px] text-xs text-slate-500">{statusDateLabel} {formatDate(statusDate)}</p> : null}
      </div>
      {projectPublicId ? (
        <Link
          className="inline-flex min-h-10 w-fit items-center gap-2 rounded-[4px] border border-slate-300 px-3 py-2 text-sm font-bold text-slate-800 transition hover:border-[#237a43] hover:text-[#237a43]"
          to={`/projects/${projectPublicId}`}
        >
          View project <ArrowForwardOutlinedIcon sx={{ fontSize: 17 }} />
        </Link>
      ) : null}
    </article>
  );
}

export default function ProfilePage() {
  const { user, viewer, logout } = useAuth();
  const [userData, setUserData] = useState(null);
  const [delegations, setDelegations] = useState([]);
  const [projectEnrollments, setProjectEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [profileError, setProfileError] = useState("");
  const [delegationError, setDelegationError] = useState("");
  const [projectError, setProjectError] = useState("");
  const [preferenceSaving, setPreferenceSaving] = useState(false);
  const [preferenceMessage, setPreferenceMessage] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [copied, setCopied] = useState(false);

  document.title = "Your Profile - Field To Market Canada";

  useEffect(() => {
    let active = true;
    setLoading(true);
    Promise.allSettled([
      axiosInstance.get("/users/me"),
      axiosInstance.get("/delegations/granted"),
      axiosInstance.get("/project-enrollments/me"),
    ]).then(([profileResult, delegationResult, projectResult]) => {
      if (!active) return;
      if (profileResult.status === "fulfilled") {
        setUserData(profileResult.value.data?.user || profileResult.value.data || null);
        setProfileError("");
      } else {
        setProfileError(apiError(profileResult.reason, "Your account details could not be loaded."));
      }
      if (delegationResult.status === "fulfilled") {
        const payload = delegationResult.value.data;
        setDelegations(Array.isArray(payload) ? payload : payload?.items || []);
        setDelegationError("");
      } else {
        setDelegationError(apiError(delegationResult.reason, "Delegated access could not be loaded."));
      }
      if (projectResult.status === "fulfilled") {
        const payload = projectResult.value.data;
        setProjectEnrollments(Array.isArray(payload) ? payload : payload?.items || []);
        setProjectError("");
      } else {
        setProjectError(apiError(projectResult.reason, "Project participation could not be loaded."));
      }
      setLoading(false);
    });
    return () => { active = false; };
  }, [user?.email]);

  const currentDelegations = useMemo(
    () => delegations.filter((item) => ["ACTIVE", "SCHEDULED"].includes(String(item.effective_status || item.status).toUpperCase())),
    [delegations]
  );
  const previousDelegations = useMemo(
    () => delegations.filter((item) => !["ACTIVE", "SCHEDULED"].includes(String(item.effective_status || item.status).toUpperCase())),
    [delegations]
  );
  const sortedProjectEnrollments = useMemo(() => {
    const priority = { OPTED_IN: 0, PENDING_APPROVAL: 1, REJECTED: 2, REVOKED: 3 };
    return [...projectEnrollments].sort((left, right) => {
      const statusDifference = (priority[left.consent_status] ?? 9) - (priority[right.consent_status] ?? 9);
      if (statusDifference !== 0) return statusDifference;
      const leftDate = new Date(left.requested_at || left.created_at || 0).getTime();
      const rightDate = new Date(right.requested_at || right.created_at || 0).getTime();
      return rightDate - leftDate;
    });
  }, [projectEnrollments]);
  const enrolledProjectCount = projectEnrollments.filter((item) => item.consent_status === "OPTED_IN").length;
  const pendingProjectCount = projectEnrollments.filter((item) => item.consent_status === "PENDING_APPROVAL").length;

  const accountName = displayName(userData, user?.name || viewer?.displayName);
  const accountEmail = userData?.email || viewer?.email || user?.email || "Not available";
  const profileId = userData?.grower_profile_public_id || viewer?.growerProfile?.publicId || "Not available";
  const notificationEnabled = Boolean(userData?.notification_preferences?.project_opportunities_email_enabled);

  async function updateNotificationPreference(enabled) {
    const previousValue = notificationEnabled;
    setPreferenceSaving(true);
    setPreferenceMessage("");
    setUserData((current) => ({
      ...current,
      notification_preferences: {
        ...current?.notification_preferences,
        project_opportunities_email_enabled: enabled,
      },
    }));
    try {
      const response = await axiosInstance.patch("/users/me/notification-preferences", {
        project_opportunities_email_enabled: enabled,
      });
      setUserData((current) => ({ ...current, notification_preferences: response.data }));
      setPreferenceMessage("Notification preference saved.");
    } catch (error) {
      setUserData((current) => ({
        ...current,
        notification_preferences: {
          ...current?.notification_preferences,
          project_opportunities_email_enabled: previousValue,
        },
      }));
      setPreferenceMessage(apiError(error, "Notification preference could not be saved."));
    } finally {
      setPreferenceSaving(false);
    }
  }

  async function copyProfileId() {
    if (!profileId || profileId === "Not available") return;
    try {
      await navigator.clipboard.writeText(profileId);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch (error) {
      setCopied(false);
    }
  }

  async function deleteAccount() {
    setDeleteBusy(true);
    setDeleteError("");
    try {
      await axiosInstance.delete("/users/me");
      setDeleteOpen(false);
      await logout();
    } catch (error) {
      setDeleteError(apiError(error, "Your portal account could not be deleted."));
    } finally {
      setDeleteBusy(false);
    }
  }

  if (loading) {
    return (
      <Page title="Your profile" headerVariant="hybrid" contentWidth="standard" padding="px-4 py-8 md:px-6 md:py-10">
        <div className="flex min-h-[360px] w-full items-center justify-center"><CircularProgress color="success" /></div>
      </Page>
    );
  }

  return (
    <Page
      title="Your profile"
      headerVariant="hybrid"
      contentWidth="standard"
      padding="px-4 py-6 md:px-6 md:py-8"
      introContent={<p>Review your account, communication preferences, and organizations authorized to work with your grower records.</p>}
    >
      <div className="w-full space-y-6">
        {profileError ? <Alert severity="error">{profileError}</Alert> : null}

        <section className="overflow-hidden rounded-[8px] border border-slate-200 bg-white shadow-[0_12px_30px_rgba(18,28,45,0.05)]">
          <div className="border-b border-slate-200 bg-[#f7faf7] px-5 py-5 md:px-6">
            <div className="flex items-center gap-4 sm:gap-5">
              <Avatar src={userData?.picture_url || user?.picture || undefined} sx={{ bgcolor: "#237a43", flexShrink: 0, fontSize: { xs: 18, sm: 25 }, fontWeight: 800, height: { xs: 48, sm: 76 }, width: { xs: 48, sm: 76 } }}>
                {initials(userData?.first_name, userData?.last_name, accountName)}
              </Avatar>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="break-words text-2xl font-black text-slate-950">{accountName}</h2>
                  <span className="rounded-[4px] border border-emerald-200 bg-emerald-50 px-2 py-1 text-xs font-bold text-emerald-800">
                    {titleCaseStatus(userData?.grower_profile_status || viewer?.growerProfile?.status)} grower profile
                  </span>
                </div>
                <p className="mt-1 hidden break-all text-sm text-slate-600 sm:block">{accountEmail}</p>
                <p className="mt-2 hidden text-sm text-slate-500 sm:block">Identity details are synchronized from your Microsoft sign-in.</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-5 px-5 py-5 sm:grid-cols-2 md:px-6">
            <Detail icon={<EmailOutlinedIcon sx={{ fontSize: 16 }} />} label="Email" value={accountEmail} />
            <Detail icon={<CalendarMonthOutlinedIcon sx={{ fontSize: 16 }} />} label="Member since" value={formatDate(userData?.created_at)} />
            <Detail icon={<ScheduleOutlinedIcon sx={{ fontSize: 16 }} />} label="Last sign-in" value={formatDate(userData?.last_sign_in, true)} />
            <Detail
              icon={<BadgeOutlinedIcon sx={{ fontSize: 16 }} />}
              label="Grower profile ID"
              value={profileId}
              action={profileId !== "Not available" ? (
                <Tooltip title={copied ? "Copied" : "Copy profile ID"}>
                  <button aria-label="Copy grower profile ID" className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-[4px] text-slate-500 hover:bg-slate-100" onClick={copyProfileId} type="button"><ContentCopyOutlinedIcon sx={{ fontSize: 17 }} /></button>
                </Tooltip>
              ) : null}
            />
          </div>
        </section>

        <section className="rounded-[8px] border border-slate-200 bg-white px-5 py-5 md:px-6">
          <div className="flex flex-col gap-4 border-b border-slate-200 pb-5 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <div className="flex items-center gap-2"><WorkspacesOutlinedIcon className="text-[#237a43]" /><h2 className="text-xl font-black text-slate-950">Project participation</h2></div>
              <p className="mt-2 max-w-[760px] text-sm leading-6 text-slate-600">Review projects you joined or requested to join, including their current status and participation timeline.</p>
            </div>
            <div className="flex shrink-0 flex-wrap gap-2">
              <span className="rounded-[4px] border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-bold text-emerald-800">{enrolledProjectCount} enrolled</span>
              {pendingProjectCount ? <span className="rounded-[4px] border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-bold text-amber-900">{pendingProjectCount} pending</span> : null}
            </div>
          </div>

          {projectError ? <Alert className="mt-4" severity="error">{projectError}</Alert> : null}

          {!projectError && sortedProjectEnrollments.length ? (
            <div className="divide-y divide-slate-200">
              {sortedProjectEnrollments.map((item) => <ProjectParticipationRow enrollment={item} key={item.public_id} />)}
            </div>
          ) : null}

          {!projectError && !sortedProjectEnrollments.length ? (
            <div className="py-8 text-center">
              <WorkspacesOutlinedIcon sx={{ color: "#64748b", fontSize: 30 }} />
              <h3 className="mt-2 font-black text-slate-800">No project participation yet</h3>
              <p className="mt-1 text-sm text-slate-500">Explore available projects to review criteria, timelines, and requested data.</p>
              <Link className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-[#237a43] hover:underline" to="/projects">Browse projects <ArrowForwardOutlinedIcon sx={{ fontSize: 17 }} /></Link>
            </div>
          ) : null}
        </section>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
          <section className="rounded-[8px] border border-slate-200 bg-white p-5 md:p-6">
            <div className="flex flex-col gap-4 border-b border-slate-200 pb-5 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <div className="flex items-center gap-2"><GroupOutlinedIcon className="text-[#237a43]" /><h2 className="text-xl font-black text-slate-950">Delegated access</h2></div>
                <p className="mt-2 max-w-[680px] text-sm leading-6 text-slate-600">Aggregators listed here can work with your grower records according to the permissions shown.</p>
              </div>
              <div className="shrink-0 rounded-[4px] border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-bold text-slate-700">
                {currentDelegations.filter((item) => String(item.effective_status || item.status).toUpperCase() === "ACTIVE").length} active
              </div>
            </div>

            <div className="mt-4 flex items-start gap-2 border-l-4 border-[#243245] bg-[#f7f8fa] px-4 py-3 text-sm leading-6 text-slate-600">
              <ShieldOutlinedIcon sx={{ color: "#243245", fontSize: 19, marginTop: "2px" }} />
              <span>Field to Market Canada administrators manage aggregator access. Contact membership@fieldtomarket.ca if a relationship or permission should change.</span>
            </div>

            {delegationError ? <Alert className="mt-4" severity="error">{delegationError}</Alert> : null}

            {!delegationError && !currentDelegations.length ? (
              <div className="mt-5 border border-dashed border-slate-300 bg-[#fafbf9] px-5 py-8 text-center">
                <LockOutlinedIcon sx={{ color: "#64748b", fontSize: 30 }} />
                <h3 className="mt-2 font-black text-slate-800">No aggregator access configured</h3>
                <p className="mt-1 text-sm text-slate-500">Only you and authorized Field to Market Canada administrators currently have access through this program.</p>
              </div>
            ) : null}

            {currentDelegations.length ? <div className="mt-5 grid grid-cols-1 gap-4">{currentDelegations.map((item) => <DelegationRow delegation={item} key={item.public_id} />)}</div> : null}

            {previousDelegations.length ? (
              <div className="mt-6 border-t border-slate-200 pt-5">
                <h3 className="text-sm font-black uppercase text-slate-600">Previous access</h3>
                <div className="mt-3 grid grid-cols-1 gap-4">{previousDelegations.map((item) => <DelegationRow delegation={item} key={item.public_id} />)}</div>
              </div>
            ) : null}
          </section>

          <div className="space-y-6">
            <section className="rounded-[8px] border border-slate-200 bg-white p-5">
              <div className="flex items-center gap-2"><NotificationsOutlinedIcon className="text-[#237a43]" /><h2 className="text-lg font-black text-slate-950">Notifications</h2></div>
              <div className="mt-4 border-t border-slate-200 pt-4">
                <FormControlLabel
                  control={<Switch checked={notificationEnabled} color="success" disabled={preferenceSaving || !userData} onChange={(event) => updateNotificationPreference(event.target.checked)} />}
                  label="Project opportunity emails"
                />
                <p className="mt-1 text-sm leading-6 text-slate-500">Receive email when a published project may match your grower profile.</p>
                {preferenceMessage ? <Alert className="mt-3" severity={preferenceMessage.includes("saved") ? "success" : "error"}>{preferenceMessage}</Alert> : null}
              </div>
            </section>

            <section className="rounded-[8px] border border-red-200 bg-white p-5">
              <div className="flex items-center gap-2"><DeleteOutlineIcon className="text-red-700" /><h2 className="text-lg font-black text-slate-950">Portal account</h2></div>
              <p className="mt-3 text-sm leading-6 text-slate-600">Delete your portal sign-in record and end this session. Agricultural records may be retained according to program and legal requirements.</p>
              <Button color="error" startIcon={<DeleteOutlineIcon />} sx={{ borderRadius: "4px", marginTop: 2 }} variant="outlined" onClick={() => setDeleteOpen(true)}>Delete portal account</Button>
            </section>
          </div>
        </div>
      </div>

      <Dialog fullWidth maxWidth="sm" open={deleteOpen} onClose={() => !deleteBusy && setDeleteOpen(false)}>
        <DialogTitle>Delete portal account?</DialogTitle>
        <DialogContent>
          <p className="text-sm leading-6 text-slate-600">This removes your Fieldprint portal user record and signs you out. Farm and program records may be retained where required. This action cannot be undone from this page.</p>
          {deleteError ? <Alert className="mt-4" severity="error">{deleteError}</Alert> : null}
        </DialogContent>
        <DialogActions sx={{ padding: "16px 24px" }}>
          <Button disabled={deleteBusy} onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button color="error" disabled={deleteBusy} startIcon={deleteBusy ? <CircularProgress color="inherit" size={16} /> : <DeleteOutlineIcon />} variant="contained" onClick={deleteAccount}>{deleteBusy ? "Deleting..." : "Delete account"}</Button>
        </DialogActions>
      </Dialog>
    </Page>
  );
}
